import moment from "moment";
import AppConstants from "../../constants";
import { EXPRESS_DELIVERY_TYPE } from "../../constants/ExpressConstants";
import {
  getUniqueValues,
  fixDecimals,
  transformIntoObj,
  transformIntoObjArrays,
  calculateTimeBetweenSlots,
  isUndefined,
  transformForScalable,
  displayUnitGrams,
  getValueFromArray,
} from "../../utils/helpers.utils";
import {
  Address,
  DeliveryInformation,
  ExpressOrdersDataResponse,
  Order,
  ProductsDataTransform,
  SupportDriver,
} from "../SharedTransform/interface";
import { getFilterAttributeValue, getActionItem, getAddressInfo } from "../SharedTransform/response.transforms";
import { TextConstants } from "constants/TextConstants";

export const getSupportDriverNames = (
  supportDrivers: SupportDriver[]
): string[] => {
  return supportDrivers.map((driver) => driver.driverName);
};

const { OUT_FOR_DELIVERY } =
  AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.ORDER_STATUS;

export const expressOrdersDataTransform = (
  response: ExpressOrdersDataResponse
): any[] => {
  const transformedArray: any[] = [];
  response.elements.forEach((order: Order) => {
    const {
      id,
      metadata,
      posInformation,
      deliveryInformation,
      orderCode,
      createdAt,
      products,
      reason,
      consignmentCode,
      orderEtaStatus,
      supportDrivers,
      containsSupportDrivers,
      assignedConsignments,
      switchingAllowed,
      switchingEnabled,
      rtdBreached,
      hasClubbingSuggestion
    } = order;
    const deliveryDate = moment(
      deliveryInformation &&
        deliveryInformation.deliverySlot &&
        deliveryInformation.deliverySlot.startTime,
      AppConstants.DATE_FORMAT_BACKEND
    );
    const customerContactInfo = (deliveryInformation &&
      deliveryInformation.customerContactInfo) || {
      address: {},
    };
    const transactionDate = moment(createdAt, AppConstants.DATE_FORMAT_BACKEND);
    //const timeBetweenSlots = calculateTimeBetweenSlots(startTime, endTime);
    transformedArray.push({
      id: consignmentCode,
      hubCode: posInformation && posInformation.store,
      hubNames: posInformation && posInformation.storeName,
      consignmentId: consignmentCode,
      status: metadata && metadata.status,
      deliveryStatus: metadata && metadata.deliveryStatus,
      deliveryArea:
        customerContactInfo && customerContactInfo.address
          ? customerContactInfo.address.area
          : "",
      deliveryDate: deliveryDate.format(AppConstants.DATE_FORMAT),
      numberOfItems: products && products.length ? products.length : 0,
      deliverySlotTime: deliveryInformation?.deliverySlot?.deliverySlotTime,
      promiseTime:
        deliveryInformation &&
        deliveryInformation.deliverySlot &&
        deliveryInformation.deliverySlot.promiseDeliveryTime
          ? deliveryInformation.deliverySlot.promiseDeliveryTime + " mins"
          : "-",
      orderId: orderCode,
      transactionDate: transactionDate.format(AppConstants.DATE_FORMAT),
      customerName: customerContactInfo.name,
      deliveryStreetAddress: getAddressInfo(customerContactInfo.address),
      deliveryZone:
        metadata && metadata?.deliveryZone ? metadata?.deliveryZone : "",
      driverName:
        deliveryInformation && deliveryInformation.driver
          ? deliveryInformation.driver.driverName
          : "",
      assignmentDate: metadata && metadata.assignmentDate,
      vehicle:
        deliveryInformation && deliveryInformation.preferredVehicle
          ? deliveryInformation.preferredVehicle
          : "",
      paymentType: metadata && metadata.paymentType,
      totalAmount: fixDecimals(
        2,
        metadata && metadata.totalAmount ? metadata.totalAmount.toString() : ""
      ),
      codAmount: fixDecimals(
        2,
        metadata && metadata.amountToCollect
          ? metadata.amountToCollect.toString()
          : ""
      ),
      collectedAmount:
        metadata && metadata.collectedAmount
          ? parseFloat(metadata.collectedAmount.toString()).toFixed(2)
          : "0.00",
      deliveryActualTime:
        metadata?.deliveryStatus === OUT_FOR_DELIVERY
          ? deliveryInformation?.deliveryDurationInMin || "-"
          : deliveryInformation?.actualTransitTimeInMin,
      deliveryExecutionStatus: deliveryInformation
        ? deliveryInformation.deliveryExecutionStatus
        : "-",
      address:
        customerContactInfo && customerContactInfo.address
          ? getAddressInfo(customerContactInfo.address)
          : "-",
      actions: getActionItem(deliveryInformation, metadata?.deliveryStatus),
      activity: metadata && metadata?.activity,
      addressStatus:
        customerContactInfo && customerContactInfo.address
          ? customerContactInfo.address.status
          : "",
      undeliveredReason: reason ? reason.reasonDescription : "",
      customerEmail: customerContactInfo?.email || "",
      orderEtaStatus: orderEtaStatus,
      supportDriverName: containsSupportDrivers
        ? getSupportDriverNames(supportDrivers)
        : [],
      assignedConsignments: assignedConsignments || [],
      switchingAllowed: switchingAllowed || false,
      inSwitchingProgress: switchingEnabled || false,
      rtdBreached: rtdBreached || false,
      hasClubbingSuggestion: hasClubbingSuggestion || false,
    });
  });
  return [...transformedArray];
};

export const driverStatusField = (fieldName: string, statusCode: string) => {
  const statuses: any =
    AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.DRIVER_STATUSES;
  const newStatus = statusCode.toUpperCase();
  return statusCode && statuses[newStatus]
    ? statuses[newStatus][fieldName]
    : "";
};

export const getOrderStatusField = (fieldName: string, statusCode: string) => {
  const statuses: any =
    AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.ORDER_STATUSES;
  return statusCode && statuses[statusCode]
    ? statuses[statusCode][fieldName]
    : "";
};

export const getDeliveryExecutionStatusField = (
  fieldName: string,
  statusCode: string
) => {
  const statuses: any =
    AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.DELIVERY_EXECUTION_STATUS;
  if (statusCode === "ON-TIME") {
    statuses[statusCode] =
      AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.DELIVERY_EXECUTION_STATUS.ON_TIME;
  }
  return statusCode && statuses[statusCode]
    ? statuses[statusCode][fieldName]
    : "";
};

export const createChipsCountFilterPayload = (
  payload: any,
  delayedToggle: boolean,
  data?: any,
  isLoadMore?: boolean,
  countryCode?: any
) => {
  payload.attributes = [];
  if (countryCode) {
    payload.countryISOCode = countryCode;
  }
  if (payload.dateRange) {
    if (payload.dateRange && payload.dateRange.value) {
      payload.isDateRange = true;
      payload.dateRange = {
        startTime: moment(
          payload.dateRange.value.startDate,
          AppConstants.DATE_FORMAT
        )
          .hours(0)
          .minutes(0)
          .seconds(0)
          .format(AppConstants.DATE_FORMAT_BACKEND),
        endTime: moment(
          payload.dateRange.value.endDate,
          AppConstants.DATE_FORMAT
        )
          .hours(23)
          .minutes(59)
          .seconds(59)
          .format(AppConstants.DATE_FORMAT_BACKEND),
      };
    }
  }
  if (delayedToggle) {
      payload.delayedOrders = delayedToggle;
  }
  if (payload.hub.value.length > 0) {
    let hubValue = payload.hub.value.map((item: any) => item.value);
    let hubNameValue = [];
    if (payload.hubName && payload.hubName.value.length) {
      hubNameValue = payload.hubName.value.map((item: any) => item.value);
    }
    hubValue = hubValue.concat(hubNameValue);
    const selectAllIdx = hubValue.indexOf(AppConstants.SELECT_ALL.value);
    const selectNoneIdx = hubValue.indexOf(AppConstants.SELECT_NONE.value);
    if (selectAllIdx > -1) {
      hubValue = [];
    }
    if (selectNoneIdx > -1) {
      hubValue = [];
    }
    if (hubValue.length) {
      payload.attributes.push({
        key: payload.hub.key,
        value: getUniqueValues(hubValue),
      });
    }
    delete payload.hub;
  } else {
    delete payload.hub;
  }
  if (payload.hubName.value.length > 0) {
    let attrValue = [];
    if (payload.hubName.value) {
      attrValue = getFilterAttributeValue(payload.hubName.value);
      payload.attributes.push({
        key: "hub",
        value: attrValue,
      });
    }
  } else {
    delete payload.hubName;
  }
  if (payload.deliveryArea.value.length > 0) {
    let attrValue = [];
    if (payload.deliveryArea.value) {
      attrValue = getFilterAttributeValue(payload.deliveryArea.value);
      payload.attributes.push({
        key: payload.deliveryArea.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.deliveryArea;
  }
  if (payload.deliveryStatus.value.length > 0) {
    let attrValue = [];
    if (payload.deliveryStatus.value) {
      attrValue = getFilterAttributeValue(payload.deliveryStatus.value);
      payload.attributes.push({
        key: payload.deliveryStatus.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.deliveryStatus;
  }

  if (payload.activity.value.length > 0) {
    let attrValue = [];
    if (payload.activity.value) {
      attrValue = getFilterAttributeValue(payload.activity.value);
      payload.attributes.push({
        key: payload.activity.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.activity;
  }
  if (payload.status.value.length > 0) {
    let attrValue = [];
    if (payload.status.value) {
      attrValue = getFilterAttributeValue(payload.status.value);
      payload.attributes.push({
        key: payload.status.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.status;
  }
  if (payload?.orderVehicleType?.value?.length > 0) {
    let attrValue = [];
    if (payload.orderVehicleType.value) {
      attrValue = getFilterAttributeValue(payload.orderVehicleType.value);
      payload.attributes.push({
        key: payload.orderVehicleType.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.orderVehicleType;
  }
  if (payload.deliveryExecutionStatus.value.length > 0) {
    let attrValue = [];
    if (payload.deliveryExecutionStatus.value) {
      attrValue = getFilterAttributeValue(
        payload.deliveryExecutionStatus.value
      );
      payload.attributes.push({
        key: payload.deliveryExecutionStatus.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.deliveryExecutionStatus;
  }
  if (payload.driverId.value.length > 0) {
    let attrValue = [];
    if (payload.driverId.value) {
      attrValue = getFilterAttributeValue(payload.driverId.value);
      payload.attributes.push({
        key: payload.driverId.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.driverId;
  }
  if (payload?.supportDriverId?.value?.length > 0) {
    let attrValue = [];

    if (payload.supportDriverId.value) {
      attrValue = getFilterAttributeValue(payload.supportDriverId.value);

      payload.attributes.push({
        key: payload.supportDriverId.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.supportDriverId;
  }
  if (payload.deliverySlotTime.value.length > 0) {
    let attrValue = [];
    if (payload.deliverySlotTime.value) {
      attrValue = getFilterAttributeValue(payload.deliverySlotTime.value);
      payload.attributes.push({
        key: payload.deliverySlotTime.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.deliverySlotTime;
  }
  if (payload.paymentType.value.length > 0) {
    let attrValue = [];
    if (payload.paymentType.value) {
      attrValue = getFilterAttributeValue(payload.paymentType.value);
      payload.attributes.push({
        key: payload.paymentType.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.paymentType;
  }

  delete payload.deliveryArea;
  delete payload.deliveryStatus;
  delete payload.status;
  delete payload.deliveryExecutionStatus;
  delete payload.deliverySlotTime;
  delete payload.driverId;
  delete payload.supportDriverId;
  delete payload.hub;
  delete payload.hubName;
  delete payload.paymentType;
  delete payload.orderVehicleType;
  delete payload.reset;
  delete payload.activity;
  if (isLoadMore && data) {
    payload.pageNumber =
      data.pageNumber + 1 < data.totalPages
        ? data.pageNumber + 1
        : payload.pageNumber;
  }
  payload.isDateRange = true;
  return payload;
};

export function formatFlatPayload(
  input: Record<string, any>,
  data?: { pageNumber: number; totalPages: number },
  options?: {
    searchAttribute?: string;
    countryISOCode?: string;
    filtersRequired?: boolean;
    pageSize?: number;
    pageNumber?: number;
  },
  isLoadMore?: boolean,
) {
  const {
    searchAttribute,
    countryISOCode = "AE",
    filtersRequired = false,
    pageSize = 20,
    pageNumber = 0
  } = options || {};

  // Convert flat input to attributes array
  const attributes = Object.entries(input)
    .filter(([key, value]) => value !== undefined && value !== null && value !== "")
    .map(([key, value]) => ({
      key,
      value: Array.isArray(value) ? value : [value]
    }));

  const payload: any = {
    pageNumber,
    pageSize,
    attributes,
    countryISOCode,
    filtersRequired
  };

  if (searchAttribute) {
    payload.searchAttribute = {
      key: 'assigneeId',
      value: searchAttribute
    };
  }

  // Infinite scroll logic
  if (isLoadMore && data ) {
    payload.pageNumber = data.pageNumber + 1 < data.totalPages ? data.pageNumber + 1 : payload.pageNumber;
  }
  return payload;
}

export const createFilterPayload = (
  payload: any,
  delayedToggle: boolean,
  data?: any,
  isLoadMore?: boolean,
  countryCode?: any,
  selectedChip?: any
) => {
  payload.attributes = [];
  if (countryCode) {
    payload.countryISOCode = countryCode;
  }
  if (payload.dateRange) {
    if (payload.dateRange && payload.dateRange.value) {
      payload.isDateRange = true;
      payload.dateRange = {
        startTime: moment(
          payload.dateRange.value.startDate,
          AppConstants.DATE_FORMAT
        )
          .hours(0)
          .minutes(0)
          .seconds(0)
          .format(AppConstants.DATE_FORMAT_BACKEND),
        endTime: moment(
          payload.dateRange.value.endDate,
          AppConstants.DATE_FORMAT
        )
          .hours(23)
          .minutes(59)
          .seconds(59)
          .format(AppConstants.DATE_FORMAT_BACKEND),
      };
    }
  }
  if (payload.hub.value.length > 0) {
    let hubValue = payload.hub.value.map((item: any) => item.value);
    let hubNameValue = [];
    if (payload.hubName && payload.hubName.value.length) {
      hubNameValue = payload.hubName.value.map((item: any) => item.value);
    }
    hubValue = hubValue.concat(hubNameValue);
    const selectAllIdx = hubValue.indexOf(AppConstants.SELECT_ALL.value);
    const selectNoneIdx = hubValue.indexOf(AppConstants.SELECT_NONE.value);
    if (selectAllIdx > -1) {
      hubValue = [];
    }
    if (selectNoneIdx > -1) {
      hubValue = [];
    }
    if (hubValue.length) {
      payload.attributes.push({
        key: payload.hub.key,
        value: getUniqueValues(hubValue),
      });
    }
    delete payload.hub;
  } else {
    delete payload.hub;
  }
  if (payload.hubName.value.length > 0) {
    let attrValue = [];
    if (payload.hubName.value) {
      attrValue = getFilterAttributeValue(payload.hubName.value);
      payload.attributes.push({
        key: "hub",
        value: attrValue,
      });
    }
  } else {
    delete payload.hubName;
  }
  if (delayedToggle) {
    payload.delayedOrders = delayedToggle;
  }
  if (payload.deliveryArea.value.length > 0) {
    let attrValue = [];
    if (payload.deliveryArea.value) {
      attrValue = getFilterAttributeValue(payload.deliveryArea.value);
      payload.attributes.push({
        key: payload.deliveryArea.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.deliveryArea;
  }
  if (payload.addressStatus.value.length > 0) {
    let attrValue = [];
    if (payload.addressStatus.value) {
      attrValue = getFilterAttributeValue(payload.addressStatus.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.addressStatus.key,
      value: attrValue,
    });
  } else {
    delete payload.addressStatus;
  }
  if (payload.activity.value.length > 0) {
    let attrValue = [];
    if (payload.activity.value) {
      attrValue = getFilterAttributeValue(payload.activity.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.activity.key,
      value: attrValue,
    });
  } else {
    delete payload.activity;
  }
  if (payload.deliveryZone.value.length > 0) {
    let attrValue = [];
    if (payload.deliveryZone.value) {
      attrValue = getFilterAttributeValue(payload.deliveryZone.value);
      payload.attributes.push({
        key: payload.deliveryZone.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.deliveryZone;
  }
  if (payload.deliveryStatus.value.length > 0) {
    let attrValue = [];
    if (payload.deliveryStatus.value) {
      attrValue = getFilterAttributeValue(payload.deliveryStatus.value);
      payload.attributes.push({
        key: payload.deliveryStatus.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.deliveryStatus;
  }
  if (payload.deliveryExecutionStatus.value.length > 0) {
    let attrValue = [];
    if (payload.deliveryExecutionStatus.value) {
      attrValue = getFilterAttributeValue(
        payload.deliveryExecutionStatus.value
      );
      payload.attributes.push({
        key: payload.deliveryExecutionStatus.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.deliveryExecutionStatus;
  }
  if (payload.status.value.length > 0) {
    let attrValue = [];
    if (payload.status.value) {
      attrValue = getFilterAttributeValue(payload.status.value);
      payload.attributes.push({
        key: payload.status.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.status;
  }
  if (payload?.orderVehicleType?.value?.length > 0) {
    let attrValue = [];
    if (payload.orderVehicleType.value) {
      attrValue = getFilterAttributeValue(payload.orderVehicleType.value);
      payload.attributes.push({
        key: payload.orderVehicleType.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.orderVehicleType;
  }
  if (payload.driverId.value.length > 0) {
    let attrValue = [];
    if (payload.driverId.value) {
      attrValue = getFilterAttributeValue(payload.driverId.value);
      payload.attributes.push({
        key: payload.driverId.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.driverId;
  }
  if (payload?.supportDriverId?.value?.length > 0) {
    let attrValue = [];

    if (payload.supportDriverId.value) {
      attrValue = getFilterAttributeValue(payload.supportDriverId.value);

      payload.attributes.push({
        key: payload.supportDriverId.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.supportDriverId;
  }
  if (payload.deliverySlotTime.value.length > 0) {
    let attrValue = [];
    if (payload.deliverySlotTime.value) {
      attrValue = getFilterAttributeValue(payload.deliverySlotTime.value);
      payload.attributes.push({
        key: payload.deliverySlotTime.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.deliverySlotTime;
  }
  if (payload.paymentType.value.length > 0) {
    let attrValue = [];
    if (payload.paymentType.value) {
      attrValue = getFilterAttributeValue(payload.paymentType.value);
      payload.attributes.push({
        key: payload.paymentType.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.paymentType;
  }
  if (selectedChip !== "UNASSIGNED") {
    if (selectedChip === "ALL") {
      const obj = {
        key: "deliveryGroupStatus",
        value: [],
      };
      payload.attributes.push(obj);
    } else {
      const obj = {
        key: "deliveryGroupStatus",
        value: [selectedChip],
      };
      payload.attributes.push(obj);
    }
  } else {
    const obj = {
      key: "deliveryGroupStatus",
      value: ["UNASSIGNED"],
    };
    payload.attributes.push(obj);
  }
  if (isLoadMore && data) {
    payload.pageNumber =
      data.pageNumber + 1 < data.totalPages
        ? data.pageNumber + 1
        : payload.pageNumber;
  }
  payload.isDateRange = true;
  delete payload.hub;
  delete payload.hubName;
  delete payload.paymentType;
  delete payload.orderVehicleType;
  delete payload.addressStatus;
  delete payload.reset;
  delete payload.deliveryArea;
  delete payload.deliveryStatus;
  delete payload.deliveryExecutionStatus;
  delete payload.status;
  delete payload.deliverySlotTime;
  delete payload.driverId;
  delete payload.supportDriverId;
  delete payload.deliveryZone;
  delete payload.activity;
  return payload;
};

export const createPayloadToFetchDriverList = (
  countryCode: any,
  pos: any,
  activity: string,
  supportDriver?: boolean
) => {
  const payload: { hubCode: any; countryCode: any; activity: string; supportDriver?: boolean } = {
    hubCode: pos && pos.store,
    countryCode: countryCode,
    activity: activity
  };
  if (supportDriver !== undefined) {
    payload.supportDriver = supportDriver;
  }

  return payload;
};

export const createPayloadToAssignDriver = (
  selectedItem: any,
  consignment: any
) => {
  const payload = {
    consignmentCode: consignment.consignmentCode,
    driverId: selectedItem?.[0],
  };
  return payload;
};

export const createPayloadToAssignSupportDriver = (
  selectedItem: any,
  consignment: any
) => {
  const payload = {
    consignmentCode: consignment.consignmentCode,
    driverIds: selectedItem,
  };
  return payload;
};

export const getWaitTimeForDriver = (checkinTime: any) => {
  const d = new Date();
  const checkTimeString = checkinTime && checkinTime.split(".")[0];
  const d2 = d.toISOString();
  const slot = moment.utc(d2).format(AppConstants.SLOT_FORMAT_BACKEND);
  const waitTime = calculateTimeBetweenSlots(
    checkTimeString && checkTimeString.split("T")[1],
    slot
  );
  return waitTime;
};

export const createExpressOrderReplanningPayload = (params: any) => {
  const payload: any = {
    consignmentCodes: [params.id],
    deliveryStatus: params.selection,
    reason: {},
  };

  // Handle reason - could be a string (reason code) or a full reason object
  if (params.reason) {
    if (typeof params.reason === 'string') {
      // If reason is a string, treat it as reasonCode only
      payload.reason = {
        reasonCode: params.reason,
        comment: params.commentsValue || '',
      };
    } else {
      // If reason is an object, use all fields
      payload.reason = {
        reasonCode: params.reason.reasonCode,
        reasonDescription: params.reason.reasonDescription,
        deliveryStatus: params.reason.deliveryStatus,
        comment: params.reason.comment || params.commentsValue || '',
        ...(params.reason.overrideReasonCode && { overrideReasonCode: params.reason.overrideReasonCode }),
      };
    }
  } else {
    // If no reason provided, set empty object
    payload.reason = {};
  }

  if (params.selection === "DELIVERED") {
    payload.comments = params.commentsValue;
    if (params.driverId) {
      payload.driverId = params.driverId;
    }
    payload.consignmentCode = params.id;
    if (params.hubCode) {
      payload.hubCode = params.hubCode;
    }
  }

  return payload;
};

export const createPayloadForReasons = (country: any, rejectionType: any, consignmentCode?: any, hubCode?: any) => {
  let deliveryType = "";
  if (rejectionType === EXPRESS_DELIVERY_TYPE.ON_HOLD) {
    deliveryType = AppConstants.EXPRESS_CONSTANTS.DELIVERY_TYPE.ON_HOLD;
  } else if (rejectionType === EXPRESS_DELIVERY_TYPE.UNDELIVERED) {
    deliveryType = AppConstants.EXPRESS_CONSTANTS.DELIVERY_TYPE.DISPATCHER;
  } else if (rejectionType === EXPRESS_DELIVERY_TYPE.DELIVERED) {
    deliveryType = AppConstants.EXPRESS_CONSTANTS.DELIVERY_TYPE.DELIVERY_COMPLETE_DISPATCHER;
  } else {
    deliveryType =
      AppConstants.EXPRESS_CONSTANTS.DELIVERY_TYPE.LINE_ITEM_REJECTION;
  }
  const payload: any = {
    countryISOCode: country,
    type: deliveryType,
  };
  if (consignmentCode) {
    payload.consignmentCode = consignmentCode;
  }
  if (hubCode) {
    payload.hubCode = hubCode;
  }
  return payload;
};

export const createPayloadForReplanDuringPartialRejection = (
  params: any,
  consignment: any,
  driverId?: string,
  reason?: any,
) => {
  const payload: any = {};
  const prodLineItems: any = {};
  const lineItems: any = {};
  if (params.length > 0) {
    params.forEach((item: any) => {
      lineItems[item.entryNumber] = {
        rejectedQty: item.rejectedQty,
      };
    });
  }
  if (params.length > 0) {
    params.forEach((item: any) => {
      prodLineItems[item.entryNumber] = {
        entryNumber: item.entryNumber,
        rejectedQty: item.rejectedQty,
        sku:
          item.substitutedProducts?.length === 1
            ? item.substitutedProducts[0]?.sku
            : item.id,
        reason: {
          reasonCode:
            item.itemDeliveryReason?.length === 1
              ? item.itemDeliveryReason[0].value
              : "",
          reasonDescription:
            item.itemDeliveryReason?.length === 1
              ? item.itemDeliveryReason[0].name
              : "",
        },
      };
    });
  }
  if (driverId) {
    payload.driverId = driverId;
  }
  payload.consignmentCodes = [consignment.consignmentCode];
  payload.lineItems = prodLineItems;
  payload.rejectedLineItems = lineItems;
  payload.deliveryStatus =
    AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.DEFAULT_STATUS_SELECTION;
  payload.reason = reason
    ? {
        reasonCode: reason.reasonCode,
        reasonDescription: reason.reasonDescription,
        deliveryStatus: reason.deliveryStatus,
        comment: reason.comment,
      }
    : {};
  return payload;
};

export const createPayloadToMarkAddressUnverified = (
  consignment: any,
  address: any
) => {
  const payload = {
    consignmentCode: consignment.consignmentCode,
    addressStatus: "UNVERIFIED",
  };
  return payload;
};

export const createPayloadToSubmitOrderComments = (
  consignment: any,
  reason: any,
  comments: string,
  desc: string
) => {
  const payload = {
    consignmentCode: consignment.consignmentCode,
    code: reason,
    description: desc,
    comment: comments,
  };
  return payload;
};
